import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Checkbox,
  Chip,
  Stack,
  Alert,
  Divider,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardActions
} from '@mui/material';
import { collection, addDoc, doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import NavBarAdm from './navbarAdm';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LinkIcon from '@mui/icons-material/Link';

// Helper function to format date from YYYY-MM-DD to DD.MM.YYYY
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}.${month}.${year}`;
};

// Helper function to format date from DD.MM.YYYY to YYYY-MM-DD for input
const formatDateToInputFormat = (dateString) => {
  if (!dateString || dateString === 'Bis auf weiteres') return '';
  const [day, month, year] = dateString.split('.');
  return `${year}-${month}-${day}`;
};

export const UmleitungAdd = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL for editing
  const isEditMode = !!id;
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    dateStart: '',
    dateEnd: '',
    description: '',
    title: '',
    number: '',
    show: true,
    image: [],
    line: []
  });

  const [imageInput, setImageInput] = useState('');
  const [lineInput, setLineInput] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [isOpenEnded, setIsOpenEnded] = useState(false);

  // Load existing data for editing
  useEffect(() => {
    const loadUmleitung = async () => {
      if (!isEditMode) return;

      setInitialLoading(true);
      try {
        const docRef = doc(db, 'Umleitungen', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const isOpenEndedDate = data.dateEnd === 'Bis auf weiteres';
          
          setFormData({
            dateStart: formatDateToInputFormat(data.dateStart) || '',
            dateEnd: isOpenEndedDate ? '' : formatDateToInputFormat(data.dateEnd) || '',
            description: data.description || '',
            title: data.title || '',
            number: data.number || '',
            show: data.show !== undefined ? data.show : true,
            image: data.image || [],
            line: data.line || []
          });
          setIsOpenEnded(isOpenEndedDate);
          setMessage('Umleitung erfolgreich geladen');
          setMessageType('success');
        } else {
          setMessage('Umleitung nicht gefunden');
          setMessageType('error');
          setTimeout(() => navigate('/adm/umleitungen'), 2000);
        }
      } catch (error) {
        console.error('Error loading document: ', error);
        setMessage(`Fehler beim Laden: ${error.message}`);
        setMessageType('error');
      } finally {
        setInitialLoading(false);
      }
    };

    loadUmleitung();
  }, [isEditMode, id, navigate]);

  // Handle form field changes
  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSwitchChange = (event) => {
    setFormData({
      ...formData,
      show: event.target.checked
    });
  };

  // Handle open-ended checkbox
  const handleOpenEndedChange = (event) => {
    const checked = event.target.checked;
    setIsOpenEnded(checked);
    
    if (checked) {
      // Clear the dateEnd field when checkbox is checked
      setFormData({
        ...formData,
        dateEnd: ''
      });
    }
  };

  // Handle array inputs (images and lines)
  const addImageUrl = () => {
    if (imageInput.trim() && !formData.image.includes(imageInput.trim())) {
      setFormData({
        ...formData,
        image: [...formData.image, imageInput.trim()]
      });
      setImageInput('');
    }
  };

  const removeImage = (indexToRemove) => {
    setFormData({
      ...formData,
      image: formData.image.filter((_, index) => index !== indexToRemove)
    });
  };

  const addLine = () => {
    if (lineInput.trim() && !formData.line.includes(lineInput.trim())) {
      setFormData({
        ...formData,
        line: [...formData.line, lineInput.trim()]
      });
      setLineInput('');
    }
  };

  const removeLine = (indexToRemove) => {
    setFormData({
      ...formData,
      line: formData.line.filter((_, index) => index !== indexToRemove)
    });
  };

  // Handle image file upload and convert to Base64
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setMessage('Bitte wählen Sie eine Bilddatei aus');
      setMessageType('error');
      return;
    }

    // Check file size (limit to 3MB for original file)
    if (file.size > 3 * 1024 * 1024) {
      setMessage('Bild ist zu groß. Maximale Größe: 3MB');
      setMessageType('error');
      return;
    }

    setImageUploading(true);

    // Create canvas for image compression
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      let maxWidth, maxHeight, quality;

      // Determine compression settings based on file size
      if (file.size > 2 * 1024 * 1024) {
        maxWidth = 1200;
        maxHeight = 900;
        quality = 0.8;
      } else if (file.size > 1 * 1024 * 1024) {
        maxWidth = 1400;
        maxHeight = 1000;
        quality = 0.85;
      } else {
        maxWidth = 1600;
        maxHeight = 1200;
        quality = 0.9;
      }

      // Calculate aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      let base64String = canvas.toDataURL('image/jpeg', quality);
      let base64Size = Math.round((base64String.length * 3) / 4);

      // Additional compression if needed
      let attempts = 0;
      while (base64Size > 700 * 1024 && attempts < 3) {
        attempts++;
        quality = Math.max(quality - 0.1, 0.4);
        
        if (attempts > 1) {
          width = Math.round(width * 0.9);
          height = Math.round(height * 0.9);
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
        }
        
        base64String = canvas.toDataURL('image/jpeg', quality);
        base64Size = Math.round((base64String.length * 3) / 4);
      }
      
      if (base64Size > 700 * 1024) {
        setImageUploading(false);
        setMessage('Bild konnte nicht ausreichend komprimiert werden.');
        setMessageType('error');
        return;
      }
      
      setFormData({
        ...formData,
        image: [...formData.image, base64String]
      });
      
      setImageUploading(false);
      setMessage(`Bild erfolgreich hinzugefügt! (${Math.round(base64Size / 1024)}KB)`);
      setMessageType('success');
      
      event.target.value = '';
    };

    img.onerror = () => {
      setImageUploading(false);
      setMessage('Fehler beim Laden des Bildes');
      setMessageType('error');
    };

    img.src = URL.createObjectURL(file);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!formData.title.trim()) {
      setMessage('Titel ist erforderlich');
      setMessageType('error');
      return;
    }

    if (!formData.dateStart) {
      setMessage('Startdatum ist erforderlich');
      setMessageType('error');
      return;
    }

    if (!isOpenEnded && !formData.dateEnd) {
      setMessage('Enddatum ist erforderlich oder aktivieren Sie "Bis auf weiteres"');
      setMessageType('error');
      return;
    }

    try {
      setLoading(true);
      
      const dataToSave = {
        ...formData,
        dateStart: formatDateToDDMMYYYY(formData.dateStart),
        dateEnd: isOpenEnded ? 'Bis auf weiteres' : formatDateToDDMMYYYY(formData.dateEnd),
        updatedAt: serverTimestamp()
      };

      if (isEditMode) {
        // Update existing document
        const docRef = doc(db, 'Umleitungen', id);
        await updateDoc(docRef, dataToSave);
        setMessage('Umleitung erfolgreich aktualisiert!');
      } else {
        // Add new document
        dataToSave.createdAt = serverTimestamp();
        await addDoc(collection(db, 'Umleitungen'), dataToSave);
        setMessage('Umleitung erfolgreich hinzugefügt!');
      }
      
      setMessageType('success');
      
      // Reset form for new entries, redirect for edits
      if (isEditMode) {
        setTimeout(() => navigate('/adm/umleitungen'), 1500);
      } else {
        setTimeout(() => {
          setFormData({
            dateStart: '',
            dateEnd: '',
            description: '',
            title: '',
            number: '',
            show: true,
            image: [],
            line: []
          });
          setIsOpenEnded(false);
          setMessage('');
        }, 2000);
      }
      
    } catch (error) {
      console.error('Error saving document: ', error);
      setMessage(`Fehler beim Speichern: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <>
        <NavBarAdm />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography>Lade Umleitung...</Typography>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <NavBarAdm />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            {isEditMode ? 'Umleitung bearbeiten' : 'Neue Umleitung hinzufügen'}
          </Typography>
          
          <Divider sx={{ mb: 3 }} />

          {message && (
            <Alert severity={messageType} sx={{ mb: 3 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <TextField
              fullWidth
              label="Titel *"
              value={formData.title}
              onChange={handleInputChange('title')}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Nummer"
              value={formData.number}
              onChange={handleInputChange('number')}
              margin="normal"
            />

            {/* Date Range */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label="Startdatum *"
                type="date"
                value={formData.dateStart}
                onChange={handleInputChange('dateStart')}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ flex: 1 }}
              />
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Enddatum *"
                  type="date"
                  value={formData.dateEnd}
                  onChange={handleInputChange('dateEnd')}
                  InputLabelProps={{ shrink: true }}
                  required={!isOpenEnded}
                  disabled={isOpenEnded}
                  sx={{ width: '100%', mb: 1 }}
                  helperText={isOpenEnded ? 'Bis auf weiteres ausgewählt' : ''}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isOpenEnded}
                      onChange={handleOpenEndedChange}
                      size="small"
                    />
                  }
                  label="Bis auf weiteres"
                  sx={{ ml: 0 }}
                />
              </Box>
            </Box>

            {/* Description */}
            <TextField
              fullWidth
              label="Beschreibung"
              value={formData.description}
              onChange={handleInputChange('description')}
              margin="normal"
              multiline
              rows={4}
            />

            {/* Visibility Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.show}
                  onChange={handleSwitchChange}
                />
              }
              label="Sichtbar"
              sx={{ mt: 2, mb: 2 }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Lines Section */}
            <Typography variant="h6" gutterBottom>
              Linien
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Linie hinzufügen"
                value={lineInput}
                onChange={(e) => setLineInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLine())}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={addLine}>
                Hinzufügen
              </Button>
            </Box>

            {formData.line.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                {formData.line.map((line, index) => (
                  <Chip
                    key={index}
                    label={line}
                    onDelete={() => removeLine(index)}
                    color="primary"
                  />
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Images Section */}
            <Typography variant="h6" gutterBottom>
              Bilder
            </Typography>

            {/* Add Image URL */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Bild-URL hinzufügen"
                value={imageInput}
                onChange={(e) => setImageInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImageUrl())}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={addImageUrl} startIcon={<LinkIcon />}>
                URL hinzufügen
              </Button>
            </Box>

            {/* Upload Image File */}
            <Box sx={{ mb: 3 }}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<AddPhotoAlternateIcon />}
                disabled={imageUploading}
              >
                {imageUploading ? 'Wird hochgeladen...' : 'Bild hochladen'}
              </Button>
            </Box>

            {/* Display Images */}
            {formData.image.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {formData.image.map((img, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={img}
                        alt={`Bild ${index + 1}`}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardActions>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeImage(index)}
                          startIcon={<DeleteIcon />}
                        >
                          Entfernen
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Submit Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/adm/umleitungen')}
                disabled={loading}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
              >
                {loading ? 'Speichern...' : isEditMode ? 'Aktualisieren' : 'Hinzufügen'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default UmleitungAdd;
